/**
 * @description
 * Renders a summary table for either a project quote or a project variation.
 * It displays cost breakdowns per stage and task, as well as a list of selected options/variations
 * along with their total sale amounts.
 * 
 * @props
 * - optionsData: Array of selected options or variations (each with cost and revision info)
 * - projectTotal: Object with overall total for a quote (non-variation mode)
 * - isVariation: Boolean flag to determine if the context is a variation or a quote
 * - stageTotal: Object mapping each stage to its aggregated cost
 * - optionTotal: Object mapping each option ID to its cost and metadata
 */
import React, { Fragment } from 'react'
import { getFormatedData } from 'src/routes/projects/task-list/task'
import idx from 'idx'

function SummaryProposal({ optionsData, projectTotal, isVariation, stageTotal, optionTotal, stages }) {
    const variationJSX = () => {
        let total = {};
        if (!isVariation) total = idx(optionTotal, _ => _.stageSummary.projectlevel.optionList) || {};
        else {
            if (Array.isArray(optionsData)) {
                optionsData.forEach(opt => {
                    total[opt.id] = optionTotal[opt.id];
                });
            }
        }

        return Object.entries(total).map(([key, option]) => {
            if (key === 'projectTotal') return null;
            const optionCost = getFormatedData(option)
            const { name = '', revisionName = '', revisionNumber = 0 } = option;
            const variationName = name + (revisionName && revisionNumber !== 1 ? ' - ' + revisionName : '');
            return (
                <tr key={option.id}>
                    <td className={`text-left ${!isVariation ? 'ps-3' : ''}`}>
                        {variationName}
                    </td>
                    <td>
                        <span className={`text-right ${!isVariation ? 'pe-2' : ''}`}>{optionCost.totalSaleAmount}</span>
                    </td>
                </tr>
            )
        })
    }

    const viewVariationsData = () => {
        return (
            <>
                {
                    variationJSX({ optionsData })
                }
            </>
        )
    }

    const total = isVariation ? getFormatedData(idx(optionTotal, _ => _.projectTotal) || {}) : getFormatedData(idx(projectTotal, _ => _) || {});

    const flattenStages = (stgs) => {
        let flat = [];
        const recurse = (stg) => {
            const stageCopy = { ...stg };
            const children = stageCopy.stages || [];
            delete stageCopy.stages;
            flat.push(stageCopy);
            children.forEach(recurse);
        };
        if (Array.isArray(stgs)) {
            stgs.forEach(recurse);
        } else if (stgs) {
            recurse(stgs);
        }
        return flat;
    };

    const flatStages = flattenStages(stages);

    const buildStageTree = (allStages, parentId = null) => {
        return allStages.filter(s => s.parent_stage_id === parentId).map(s => ({
            ...s,
            children: buildStageTree(allStages, s.id),
        }));
    };

    const stageTree = buildStageTree(flatStages);

    const renderStage = (stage, level = 0) => {
        const section = stageTotal[stage.id];
        if (!section) return [];

        const stagesCost = getFormatedData(section);
        const isTopLevel = level === 0;
        const stageClass = isTopLevel ? 'btn-bg-brand-3 text-white' : '';
        const noOfStages = stage?.stages?.length || 0;
        const stagePadding = isTopLevel ?
            'ps-3' :
            `ps-${(level > 1 ?
                level + 1
                : level
            ) * 4}`;
        const stageTotalPadding = level <= 1 ?
            'pe-2' :
            `pe-${(level > 1 ?
                (1 + 2) // to make sure the total padding is 3 when level is more than 1
                : level + 2) * 7
            }`;

        let rows = [
            <tr key={stage.id} className="stage-row border-bottom-0">
                <td className={`text-left ${stagePadding} ${stageClass}`} data-cy="stage-name">
                    <b>{stage.name || section.stageHierarchy}</b>
                </td>
                <td className={stageClass}>
                    <span className={`text-right ${stageTotalPadding}`} data-cy="stage-total">
                        {stagesCost.totalSaleAmount}
                    </span>
                </td>
            </tr>
        ];

        if (stage.children && stage.children.length > 0) {
            stage.children.forEach(child => {
                rows = rows.concat(renderStage(child, level + 1));
            });
        }

        // Render tasks under this stage conditionally
        if (section.taskList?.tasks && (isVariation || level === 0)) {
            Object.entries(section.taskList.tasks).forEach(([taskId, task], index) => {
                const taskCost = getFormatedData(task);
                const taskPadding = isTopLevel && noOfStages === 0 ?
                    'ps-3' :
                    `ps-${(level > 1 ?
                        (level + 1)
                        : level
                    ) * 5}`;
                const taskTotalPadding = isTopLevel && noOfStages === 0 ?
                    'pe-2' :
                    `pe-${(level > 1 ?
                        (1 + 2) // to make sure the total padding is 3 when level is more than 1
                        : level + 2) * 7
                    }`;
                rows.push(
                    <tr key={taskId} className={`${index === 0 ? 'border-top-0' : ''}`}>
                        <td className={`text-left ${taskPadding}`} data-cy="task-name">
                            <b>{task.name || `Task ${taskId}`}</b>
                        </td>
                        <td>
                            <span className={`text-right ${taskTotalPadding}`} data-cy="task-total">
                                {taskCost.totalSaleAmount}
                            </span>
                        </td>
                    </tr>
                );
            });
        }

        return rows;
    };

    return (
        <div className="col-12 my-3 table-responsive">
            <table className={`table-create mb-0 table table-bordered fw-bolder ${isVariation ? 'table-row-color' : ''}`}>
                <thead>
                    <tr>
                        <th className={`w-auto text-left ${isVariation ? '' : 'ps-3'}`}>Description</th>
                        <th className={`text-right ${isVariation ? '' : 'pe-5'}`}>Total</th>
                    </tr>
                </thead>
                <tbody>
                    {stageTree.map(stage => renderStage(stage))}
                    {isVariation && viewVariationsData()}
                    <tr>
                        <td className='text-right'>
                            <span className="tableinfo">Total</span>
                        </td>
                        <td>
                            <span className={`tableinfo text-right ${isVariation ? '' : 'pe-2'}`}>{total.totalSaleAmount}</span>
                        </td>
                    </tr>
                    {(!isVariation && Array.isArray(optionsData) && optionsData.length > 0 && viewVariationsData()) || ''}
                </tbody>
            </table>
        </div>
    )
}

export default SummaryProposal